import React, { useState } from 'react';
import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { Link, } from 'react-router-dom';
import Loader from './../components/Loader';
import Message from './../components/Message';

import {
  useGetDepositsQuery,
  useDeleteDepositMutation,
  useCreateDepositMutation,
} from './../slices/depositsApiSlice';
import { toast } from 'react-toastify';

const BankUserList = () => {
  
  const [pageNumber, setPageNumber] = useState(1);
  const { data, isLoading, error, refetch } = useGetDepositsQuery({ pageNumber });
  const [deleteDeposit, { isLoading: loadingDelete }] = useDeleteDepositMutation();
  const [createDeposit, { isLoading: loadingCreate }] = useCreateDepositMutation();

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Create a new deposit
  const createDepositHandler = async () => {
    if (window.confirm('Are you sure you want to create a new deposit?')) {
      try {
        const newDeposit = {
          date: new Date().toISOString().split('T')[0],
          account: 'Debit',
          accountName: 'Default Account',
          subAccount: 'Default Sub Account',
          details: 'New Deposit',
          amount: 0,
          status: 'Pending',
          category: 'Other',
          notes: 'Default note',
        
        };
        await createDeposit(newDeposit).unwrap();
        toast.success('New deposit created successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Delete an deposit
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this deposit?')) {
      try {
        await deleteDeposit(id).unwrap();
        toast.success('Deposit deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  // Filter and sort deposits
  const filterAndSortDeposits = () => {
    let filteredDeposits = [...(data?.deposits || [])];

    // Filter by text input
    if (filter) {
      filteredDeposits = filteredDeposits.filter(
        (deposit) =>
          deposit.account?.toLowerCase().includes(filter.toLowerCase()) ||
          deposit.accountName?.toLowerCase().includes(filter.toLowerCase()) ||
          deposit.details?.toLowerCase().includes(filter.toLowerCase()) ||
          deposit.category?.toLowerCase().includes(filter.toLowerCase()) ||
          deposit.subAccount?.toLowerCase().includes(filter.toLowerCase()) ||
          deposit.notes?.toLowerCase().includes(filter.toLowerCase()) ||
          deposit.username?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Filter by date range
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filteredDeposits = filteredDeposits.filter((deposit) => {
        const depositDate = new Date(deposit.date);
        return depositDate >= start && depositDate <= end;
      });
    }

    // Sort deposits
    if (sortField) {
      filteredDeposits.sort((a, b) => {
        if (sortField === 'amount') {
          return sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount;
        } else if (sortField === 'date') {
          return sortOrder === 'asc'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else {
          const aValue = a[sortField]?.toLowerCase() || '';
          const bValue = b[sortField]?.toLowerCase() || '';
          return sortOrder === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
      });
    }

    return filteredDeposits;
  };

  // Calculate total for date range
  const calculateTotalForDateRange = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);

    const filteredDeposits = data?.deposits?.filter((deposit) => {
      const depositDate = new Date(deposit.date);
      return depositDate >= start && depositDate <= end;
    }) || [];

    return filteredDeposits.reduce((acc, deposit) => acc + deposit.amount, 0);
  };

  // Calculate total for all deposits
  const totalAmount = data?.deposits?.reduce((acc, deposit) => acc + deposit.amount, 0) || 0;

   // Handle pagination changes
  const paginationHandler = (page) => {
    setPageNumber(page);
  };
  
  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Bank Trans</h1>
        </Col>
        <Col className="text-end">
          <Button onClick={createDepositHandler} className="my-3" style={{display:"none"}}>
            <FaPlus /> Add New Deposit
          </Button>
          <Link to='/create/staff/deposit' className='btn btn-light my-3'>
          Add Data
          </Link>
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col>
          <h5>Total Amount: D{totalAmount.toFixed(2)}</h5>
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by account, name, details, category, subAccount, or notes"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
        </Col>
        <Col md={4}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </Col>
        <Col md={4}>
          <h5>
            Total (Filtered Range): D{calculateTotalForDateRange().toFixed(2)}
          </h5>
        </Col>
      </Row>
  
      {loadingCreate && <Loader />}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'An error occurred'}</Message>
      ) : data?.deposits?.length > 0 ? (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                  Date {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>Account</th>
                <th onClick={() => handleSort('accountName')} style={{ cursor: 'pointer' }}>
                  Account Name {sortField === 'accountName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('details')} style={{ cursor: 'pointer' }}>
                  Details {sortField === 'details' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer' }}>
                  Amount {sortField === 'amount' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                  Category {sortField === 'category' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('notes')} style={{ cursor: 'pointer' }}>
                  Notes {sortField === 'notes' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th>Images</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortDeposits().map((deposit) => (
                <tr key={deposit._id}>
                  <td>{new Date(deposit.date).toLocaleDateString()}</td>
                  <td>{deposit.account}</td>
                  <td>{deposit.accountName}</td>
                  <td>{deposit.details}</td>
                  <td>
                    {deposit.amount >= 0
                      ? `${deposit.amount.toFixed(2)}`
                      : `-${Math.abs(deposit.amount).toFixed(2)}`}
                  </td>
                  <td>{deposit.category}</td>
                  <td>{deposit.notes}</td>
                  
                  <td className="thumbnails">
                    {deposit.images?.length > 0 && (
                      <img
                        src={deposit.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                          width: '15px',
                          height: '15px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </td>

                  <td>
                    <Link
                       className="btn btn-primary btn-sm m-2"
                      to={`/deposit/${deposit._id}`}
                        role="button"
                    >
                    Details
                   </Link>
                    <Button
                     style={{display:"none"}}
                      as={Link}
                      to={`/deposit/${deposit._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      style={{ display: 'none' }}
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(deposit._id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Pagination>
                       {[...Array(data.pages).keys()].map((x) => (
                          <Pagination.Item
                          key={x + 1}
                          active={x + 1 === pageNumber}
                          onClick={() => paginationHandler(x + 1)}
                         >
                        {x + 1}
                        </Pagination.Item>
                        ))}
          </Pagination>
        </>
      ) : (
        <Message variant="info">No deposits found</Message>
      )}
    </>
  );
};

export default BankUserList;